import React, { useState, useCallback, useMemo, useEffect } from 'react';
import ReactFlow, {
 Background,
 Controls,
 Edge,
 Node,
 Connection,
 useNodesState,
 useEdgesState,
 Handle,
 Position,
 NodeProps,
 MarkerType,
 BackgroundVariant
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import { useSimpleApp } from '../context/SimpleAppContext';
import { OrgChartPerson } from '../types/scorecard';

// Custom Node Component
const PersonNode = ({ data, id }: NodeProps) => {
 const { person, isAdmin, onEdit, onDelete, onAddSubordinate } = data;
 const isEditing = person.id === data.editingId;

 // Visual state based on hired status
 const cardClasses = `
  group relative p-3 rounded-xl text-center min-w-[200px] shadow-lg transition-all border-2
  ${person.isHired
   ? 'bg-cardio-600 border-cardio-500 shadow-cardio-100'
   : 'bg-white border-dashed border-gray-300 shadow-gray-50'}
  ${isEditing ? 'ring-4 ring-cardio-200 !border-cardio-400' : ''}
  hover:shadow-xl hover:border-cardio-400
 `;

 const textClasses = person.isHired ? 'text-white' : 'text-gray-900';
 const subTextClasses = person.isHired ? 'text-blue-100' : 'text-gray-500';

 return (
  <div className={cardClasses}>
   {/* Input Handle (Top) */}
   <Handle
    type="target"
    position={Position.Top}
    className={`w-3 h-3 !bg-cardio-400 border-2 border-white transition-opacity duration-300 ${!isAdmin ? '!opacity-0 pointer-events-none' : ''}`}
   />

   {isEditing ? (
    <div className="space-y-2 p-1 text-left">
     <div className="text-[10px] font-bold text-gray-400 uppercase mb-1">Editing Member</div>
     <input
      autoFocus
      type="text"
      value={data.editingName}
      onChange={(e) => data.setEditingName(e.target.value)}
      className="w-full px-2 py-1.5 text-xs text-black border rounded-lg focus:ring-2 focus:ring-cardio-500 outline-none shadow-sm"
      placeholder="Name"
     />
     <input
      type="text"
      value={data.editingTitle}
      onChange={(e) => data.setEditingTitle(e.target.value)}
      className="w-full px-2 py-1.5 text-xs text-black border rounded-lg focus:ring-2 focus:ring-cardio-500 outline-none shadow-sm"
      placeholder="Title"
     />
     <div className="flex items-center justify-between mt-3 bg-gray-50 p-2 rounded-lg">
      <label className="flex items-center space-x-2 text-[10px] text-gray-700 font-medium cursor-pointer">
       <input
        type="checkbox"
        checked={data.editingIsHired}
        onChange={(e) => data.setEditingIsHired(e.target.checked)}
        className="rounded-md h-4 w-4 text-cardio-600 focus:ring-cardio-500 border-gray-300"
       />
       <span>Mark as Hired</span>
      </label>
      <div className="flex space-x-1">
       <button
        onClick={(e) => { e.stopPropagation(); data.onSave(); }}
        className="p-1.5 bg-cardio-600 text-white rounded-md hover:bg-cardio-700 transition-colors shadow-sm"
        title="Save Changes"
       >
        <Save size={14} />
       </button>
       <button
        onClick={(e) => { e.stopPropagation(); data.onCancel(); }}
        className="p-1.5 bg-gray-400 text-white rounded-md hover:bg-gray-500 transition-colors"
        title="Cancel"
       >
        <X size={14} />
       </button>
      </div>
     </div>
    </div>
   ) : (
    <>
     <div className="py-1">
      <div className={`font-bold text-[14px] leading-tight mb-1 truncate px-2 ${textClasses}`}>
       {person.name || 'Untitled Role'}
      </div>
      <div className={`text-[11px] font-medium leading-tight opacity-90 px-2 ${subTextClasses}`}>
       {person.title || 'Tap to edit title'}
      </div>
     </div>

     {isAdmin && (
      <>
       {/* Hover Actions (Edit/Delete) */}
       <div className="absolute -top-3 -right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-all transform translate-y-2 group-hover:translate-y-0 z-20">
        <div className="flex gap-1 bg-white p-1 rounded-xl shadow-lg border border-gray-100">
         <button
          onClick={(e) => { e.stopPropagation(); onEdit(person); }}
          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
          title="Edit Member"
         >
          <Edit2 size={14} />
         </button>
         <button
          onClick={(e) => { e.stopPropagation(); onDelete(person.id); }}
          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          title="Remove Member"
         >
          <Trash2 size={14} />
         </button>
        </div>
       </div>

       {/* High-Visibility Add Button at bottom */}
       <div className="mt-3 pt-2 border-t border-black/5 flex justify-center">
        <button
         onClick={(e) => { e.stopPropagation(); onAddSubordinate(person.id, person.level + 1, person.position); }}
         className={`
          flex items-center space-x-1 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider
          transition-all active:scale-95
          ${person.isHired
           ? 'bg-white/20 text-white hover:bg-white/30'
           : 'bg-cardio-50 text-cardio-600 hover:bg-cardio-100 border border-cardio-100'}
         `}
        >
         <Plus size={12} strokeWidth={3} />
         <span>Add Person</span>
        </button>
       </div>
      </>
     )}
    </>
   )}

   {/* Output Handle (Bottom) */}
   <Handle
    type="source"
    position={Position.Bottom}
    className={`w-3 h-3 !bg-cardio-400 border-2 border-white transition-opacity duration-300 ${!isAdmin ? '!opacity-0 pointer-events-none' : ''}`}
   />
  </div>
 );
};

const nodeTypes = {
 person: PersonNode,
};

export function OrgChart() {
 const {
  orgChart,
  authState,
  updateOrgChartPerson,
  addOrgChartPerson,
  removeOrgChartPerson
 } = useSimpleApp();

 const [editingId, setEditingId] = useState<string | null>(null);
 const [editingName, setEditingName] = useState('');
 const [editingTitle, setEditingTitle] = useState('');
 const [editingIsHired, setEditingIsHired] = useState(true);

 const isAdmin = authState.user?.role === 'admin';

 // Map People to Nodes
 const initialNodes: Node[] = useMemo(() => {
  return orgChart.people.map((person) => ({
   id: person.id,
   type: 'person',
   position: person.position || { x: 100, y: 100 },
   data: {
    person,
    isAdmin,
    editingId,
    editingName,
    editingTitle,
    editingIsHired,
    setEditingName,
    setEditingTitle,
    setEditingIsHired,
    onEdit: (p: OrgChartPerson) => {
     setEditingId(p.id);
     setEditingName(p.name);
     setEditingTitle(p.title);
     setEditingIsHired(p.isHired);
    },
    onCancel: () => setEditingId(null),
    onSave: () => {
     if (editingId) {
      updateOrgChartPerson(editingId, {
       name: editingName,
       title: editingTitle,
       isHired: editingIsHired
      });
      setEditingId(null);
     }
    },
    onDelete: (id: string) => {
     if (window.confirm('Are you sure you want to remove this position? (Note: Subordinates will remain but lose their connection)')) {
      removeOrgChartPerson(id);
     }
    },
    onAddSubordinate: (parentId: string, level: number, parentPos?: { x: number, y: number }) => {
     // Offsetting the new node slightly below and to the right
     const newPos = parentPos
      ? { x: parentPos.x + 50, y: parentPos.y + 160 }
      : { x: 50, y: 50 };

     addOrgChartPerson({
      name: 'New Person',
      title: 'Role Description',
      isHired: false,
      parentId,
      level,
      position: newPos
     });
    }
   },
   // Draggable property based on Admin status
   draggable: isAdmin && !editingId
  }));
 }, [orgChart.people, isAdmin, editingId, editingName, editingTitle, editingIsHired, updateOrgChartPerson, removeOrgChartPerson, addOrgChartPerson]);

 // Map Relationships to Edges
 const initialEdges: Edge[] = useMemo(() => {
  return orgChart.people
   .filter((p) => p.parentId)
   .map((p) => ({
    id: `e-${p.parentId}-${p.id}`,
    source: p.parentId!,
    target: p.id,
    type: 'smoothstep',
    animated: false,
    style: { stroke: '#94A3B8', strokeWidth: 2.5 },
    markerEnd: {
     type: MarkerType.ArrowClosed,
     color: '#94A3B8',
     width: 15,
     height: 15,
    },
   }));
 }, [orgChart.people]);

 const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
 const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

 // Keep internal React Flow state in sync with context data
 useEffect(() => {
  setNodes(initialNodes);
 }, [initialNodes, setNodes]);

 useEffect(() => {
  setEdges(initialEdges);
 }, [initialEdges, setEdges]);

 // Save position whenever dragging stops
 const onNodeDragStop = useCallback((event: any, node: Node) => {
  updateOrgChartPerson(node.id, {
   position: node.position
  });
 }, [updateOrgChartPerson]);

 // Handle manual connection between nodes
 const onConnect = useCallback(
  (params: Connection) => {
   if (params.source && params.target) {
    updateOrgChartPerson(params.target, {
     parentId: params.source
    });
   }
  },
  [updateOrgChartPerson]
 );

 return (
  <div className="flex-1 flex flex-col min-h-0 bg-slate-50 overflow-hidden">
   {/* Full Screen Flow Canvas */}
   <div className="flex-1 min-h-0 relative touch-manipulation">
    <ReactFlow
     nodes={nodes}
     edges={edges}
     onNodesChange={onNodesChange}
     onEdgesChange={onEdgesChange}
     onNodeDragStop={onNodeDragStop}
     onConnect={onConnect}
     nodeTypes={nodeTypes}
     fitView
     fitViewOptions={{ padding: 0.2 }}
     attributionPosition="bottom-right"
     minZoom={0.1}
     maxZoom={2.0}
    >
     {isAdmin && (
      <Background
       variant={BackgroundVariant.Lines}
       gap={40}
       color="#f1f5f9"
       size={1}
      />
     )}
     {isAdmin && (
      <Controls
       className="bg-white rounded-2xl shadow-2xl border-none p-1 overflow-hidden"
       showInteractive={false}
      />
     )}
    </ReactFlow>
   </div>

   {/* Global CSS for Flow Styling */}
   <style dangerouslySetInnerHTML={{
    __html: `
    .react-flow__node {
     padding: 0;
     background: transparent;
     border: none;
     cursor: grab;
     transition: transform 0.15s ease-out;
    }
    .react-flow__node:active {
     cursor: grabbing;
    }
    .react-flow__handle {
     width: 10px !important;
     height: 10px !important;
     background: #0ea5e9 !important;
     border: 2px solid white !important;
     box-shadow: 0 4px 6px -1px rgb(0 0 0 / 0.1);
     z-index: 50;
    }
    .react-flow__handle:hover {
     transform: scale(1.3);
     background: #0284c7 !important;
    }
    .react-flow__edge-path {
     stroke: #94A3B8;
     stroke-width: 3px;
     filter: drop-shadow(0 1px 1px rgb(0 0 0 / 0.05));
    }
    .react-flow__controls button {
     border-bottom: 1px solid #f1f5f9;
     color: #64748b;
     height: 32px;
     width: 32px;
     display: flex;
     align-items: center;
     justify-content: center;
    }
    .react-flow__controls button:hover {
     background-color: #f8fafc;
     color: #0ea5e9;
    }
    .react-flow__attribution {
     background: rgba(255, 255, 255, 0.5);
     padding: 2px 8px;
     border-radius: 4px;
     font-size: 9px;
    }
   `}} />
  </div>
 );
}